import { useMemo } from "react";
import { Descriptions, Divider, Flex, Tag } from "antd";
import { useAuth } from "../../../../store/context/AuthContext";
import { formatCurrency, formatDateTime } from "../../../../utilities/utils";

const SectionHeader = ({ title }) => (
  <Divider
    orientation="left"
    plain
    variant="dotted"
    style={{ borderColor: "#7cb305", marginBottom: "5px" }}
  >
    <h4>{title}</h4>
  </Divider>
);

function DetailView({ data }) {
  const { complaint, ticket, sms = [], bankStatements = [] } = data || {};
  const { reference } = useAuth();
  const complainRef = complaint?.reference || {};

  // ---------- Helpers ----------
  const refMap = useMemo(() => {
    const m = {};
    (reference || []).forEach((r) => {
      m[r.reference_key] = r.name ?? r.title ?? r.reference_key;
    });
    return m;
  }, [reference]);

  const refLabel = (key) => refMap[key] ?? key ?? "-";

  const statusTag = (status, dict = "generic") => {
    const s = String(status || "").toLowerCase();

    // Өргөн хэрэглээний статусууд
    if (dict === "complaint") {
      const map = {
        pending: { color: "orange", text: "Хүлээгдэж байна" },
        resolved: { color: "green", text: "Шийдэгдсэн" },
        rejected: { color: "red", text: "Татгалзсан" },
      };
      const meta = map[s] || {
        color: "default",
        text: status || "Тодорхойгүй",
      };
      return (
        <Tag color={meta.color} style={{ fontSize: 13 }}>
          {meta.text}
        </Tag>
      );
    }

    if (dict === "ticket") {
      const map = {
        created: { color: "orange", text: "Үүссэн" },
        pending: { color: "orange", text: "Хүлээгдэж байна" },
        paid: { color: "green", text: "Төлөгдсөн" },
        canceled: { color: "red", text: "Цуцлагдсан" },
        failed: { color: "red", text: "Амжилтгүй" },
      };
      const meta = map[s] || {
        color: "default",
        text: status || "Тодорхойгүй",
      };
      return (
        <Tag color={meta.color} style={{ fontSize: 13 }}>
          {meta.text}
        </Tag>
      );
    }

    if (dict === "invoice" || dict === "payment") {
      const map = {
        created: { color: "orange", text: "Үүссэн" },
        pending: { color: "orange", text: "Хүлээгдэж байна" },
        paid: { color: "green", text: "Төлөгдсөн" },
        canceled: { color: "red", text: "Цуцлагдсан" },
        failed: { color: "red", text: "Амжилтгүй" },
      };
      const meta = map[s] || {
        color: "default",
        text: status || "Тодорхойгүй",
      };
      return (
        <Tag color={meta.color} style={{ fontSize: 13 }}>
          {meta.text}
        </Tag>
      );
    }

    if (dict === "sms") {
      const map = {
        canceled: { color: "red", text: "Цуцалсан" },
        pending: { color: "orange", text: "Хүлээгдэж байна" },
        sent: { color: "green", text: "Илгээгдсэн" },
        failed: { color: "red", text: "Амжилтгүй" },
      };
      const meta = map[s] || {
        color: "default",
        text: status || "Тодорхойгүй",
      };
      return (
        <Tag color={meta.color} style={{ fontSize: 13 }}>
          {meta.text}
        </Tag>
      );
    }

    if (dict === "bank") {
      const map = {
        success: { color: "green", text: "Амжилттай" },
        fractional_amount: { color: "orange", text: "Бутархай дүн" },
        insufficient_amount: { color: "red", text: "Мөнгөн дүн буруу" },
        exceeds_available: { color: "red", text: "Үлдэгдэл хүрэлцэхгүй" },
        limit_exceeded: { color: "orange", text: "Хязгаар давсан" },
        invalid_phone: { color: "red", text: "Утас буруу" },
        invalid_lottery: { color: "red", text: "Сугалаа буруу" },
        internal_error: { color: "red", text: "Дотоод алдаа" },
      };
      const meta = map[s] || {
        color: "default",
        text: status || "Тодорхойгүй",
      };
      return (
        <Tag color={meta.color} style={{ fontSize: 13 }}>
          {meta.text}
        </Tag>
      );
    }

    const meta = { color: "default", text: status || "Тодорхойгүй" };
    return (
      <Tag color={meta.color} style={{ fontSize: 13 }}>
        {meta.text}
      </Tag>
    );
  };

  const methodLabel = (m) => {
    const v = String(m || "").toLowerCase();
    const map = {
      qpay: "QPay",
      bank: "Банк",
      manual: "Гар аргаар",
    };
    return map[v] || m || "-";
  };

  const joinNumbers = (nums) =>
    (nums || [])
      .map((e) => String(e?.number ?? e ?? ""))
      .map((s) => s.split("-").pop())
      .join(", ") || "-";

  // ---------- Render ----------
  return (
    <Flex vertical gap={16}>
      {/* Гомдол */}
      <section>
        <SectionHeader title={"Гомдол"} />
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Гарчиг" style={{ fontSize: 13 }}>
            {refLabel(complaint?.title)}
          </Descriptions.Item>
          <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
            {statusTag(complaint?.status, "complaint")}
          </Descriptions.Item>
          <Descriptions.Item label="Утас" style={{ fontSize: 13 }}>
            {complaint?.phone || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Үүсгэсэн төрөл" style={{ fontSize: 13 }}>
            {complainRef?.complainType || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Үүссэн" style={{ fontSize: 13 }}>
            {formatDateTime(complaint?.created_at) || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Тайлбар" span={2} style={{ fontSize: 13 }}>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 13 }}>
              {complaint?.description || "-"}
            </pre>
          </Descriptions.Item>
          <Descriptions.Item
            label="Хэрэглэгчийн хүсэлт"
            style={{ fontSize: 13 }}
          >
            {refLabel(complainRef?.user_request)}
          </Descriptions.Item>
          <Descriptions.Item label="Сугалааны код" style={{ fontSize: 13 }}>
            {complainRef?.ticket_prefix ||
              complainRef?.lottery?.ticket_prefix ||
              "-"}
          </Descriptions.Item>
          {["resolved", "rejected"].includes(complaint?.status) &&
            complaint?.comment && (
              <Descriptions.Item
                label={
                  complaint?.status === "resolved"
                    ? "Шийдсэн товч тайлбар"
                    : "Татгалзсан шалтгаан"
                }
                span={2}
                style={{ fontSize: 13 }}
              >
                <pre
                  style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 13 }}
                >
                  {complaint?.comment || "-"}
                </pre>
              </Descriptions.Item>
            )}
        </Descriptions>
      </section>

      {/* Тасалбар */}
      {ticket && (
        <section>
          <SectionHeader title={"Тасалбар"} />
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Сугалааны нэр" style={{ fontSize: 13 }}>
              {ticket.Lottery?.title || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label="Тасалбарын төлөв"
              style={{ fontSize: 13 }}
            >
              {statusTag(ticket?.status, "ticket")}
            </Descriptions.Item>
            <Descriptions.Item label="Код" style={{ fontSize: 13 }}>
              {ticket.Lottery?.ticket_prefix || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Үүссэн огноо" style={{ fontSize: 13 }}>
              {formatDateTime(ticket?.created_at) || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label="Дугаарын дараалал"
              style={{ fontSize: 13 }}
            >
              {ticket.LotteryNumbers?.[0]?.order ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label="Сугалааны дугаар"
              span={2}
              style={{ fontSize: 13 }}
            >
              {joinNumbers(ticket?.LotteryNumbers?.map((n) => n.number))}
            </Descriptions.Item>
          </Descriptions>
        </section>
      )}

      {/* Нэхэмжлэл
      {ticket?.Invoice && (
        <section>
          <h4>Нэхэмжлэл</h4>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Дүн" style={{ fontSize: 13 }}>
              {ticket.Invoice.amount
                ? formatCurrency(ticket.Invoice.amount)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
              {statusTag(ticket.Invoice.status, "invoice")}
            </Descriptions.Item>
            <Descriptions.Item label="Арга" style={{ fontSize: 13 }}>
              {methodLabel(ticket.Invoice.method)}
            </Descriptions.Item>
            <Descriptions.Item label="Төлөх хугацаа" style={{ fontSize: 13 }}>
              {ticket.Invoice.due_date
                ? formatDateTime(ticket.Invoice.due_date)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Төлөгдсөн огноо" style={{ fontSize: 13 }}>
              {ticket.Invoice.paid_at
                ? formatDateTime(ticket.Invoice.paid_at)
                : "-"}
            </Descriptions.Item>
          </Descriptions>
        </section>
      )} */}

      {/* Төлбөр(үүд) */}
      {Array.isArray(ticket?.Payments) && ticket.Payments.length > 0 && (
        <section>
          <SectionHeader title={"Төлбөр"} />
          <Flex vertical gap={8}>
            {ticket.Payments.map((p) => (
              <Descriptions key={p.id} column={2} bordered size="small">
                <Descriptions.Item label="Дүн" style={{ fontSize: 13 }}>
                  {p.amount ? formatCurrency(p.amount) : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
                  {statusTag(p.status, "payment")}
                </Descriptions.Item>
                <Descriptions.Item label="Арга" style={{ fontSize: 13 }}>
                  {methodLabel(p.method)}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Гүйлгээний ID"
                  style={{ fontSize: 13 }}
                >
                  {p.transaction_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Үүссэн" style={{ fontSize: 13 }}>
                  {formatDateTime(p.created_at) || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Төлөгдсөн огноо"
                  style={{ fontSize: 13 }}
                >
                  {p.paid_at ? formatDateTime(p.paid_at) : "-"}
                </Descriptions.Item>
              </Descriptions>
            ))}
          </Flex>
        </section>
      )}

      {/* Мессеж(үүд) */}
      {Array.isArray(sms) && sms.length > 0 && (
        <section>
          <SectionHeader title={"Мессеж"} />
          <Flex vertical gap={8}>
            {sms.map((s) => (
              <Descriptions key={s.id} column={2} bordered size="small">
                <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
                  {statusTag(s.status, "sms")}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Үйлчилгээ үзүүлэгч"
                  style={{ fontSize: 13 }}
                >
                  {s.sms_provider || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Дугаар (илгээгч)"
                  style={{ fontSize: 13 }}
                >
                  {s.from || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Дугаар (хүлээн авагч)"
                  style={{ fontSize: 13 }}
                >
                  {s.to || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Огноо" style={{ fontSize: 13 }}>
                  {formatDateTime(s.created_at) || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Мессеж"
                  span={2}
                  style={{ fontSize: 13 }}
                >
                  <pre
                    style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 13 }}
                  >
                    {s.message || "-"}
                  </pre>
                </Descriptions.Item>
              </Descriptions>
            ))}
          </Flex>
        </section>
      )}

      {/* Банк(ын хуулга) */}
      {Array.isArray(bankStatements) && bankStatements.length > 0 && (
        <section>
          <SectionHeader title={"Банк"} />
          <Flex vertical gap={8}>
            {bankStatements.map((b) => (
              <Descriptions key={b.id} column={2} bordered size="small">
                <Descriptions.Item
                  label="Гүйлгээний ID"
                  style={{ fontSize: 13 }}
                >
                  {b.record_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Данс" style={{ fontSize: 13 }}>
                  {b.account_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Дүн" style={{ fontSize: 13 }}>
                  {b.amount ? formatCurrency(b.amount) : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Огноо" style={{ fontSize: 13 }}>
                  {formatDateTime(b.date_at) || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
                  {statusTag(b.purchase_status, "bank")}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Худалдан авалт"
                  style={{ fontSize: 13 }}
                >
                  {b.purchase_comment}
                </Descriptions.Item>

                <Descriptions.Item
                  label="Хэрэглэгчийн данс"
                  style={{ fontSize: 13 }}
                >
                  {b.raw_data?.relatedAccount ?? "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Тайлбар"
                  span={2}
                  style={{ fontSize: 13 }}
                >
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      fontSize: 13,
                    }}
                  >
                    {b.description || "-"}
                  </p>
                </Descriptions.Item>
              </Descriptions>
            ))}
          </Flex>
        </section>
      )}

      {/* --- ГАРААР ҮҮСГЭСЭН ГОМДОЛД БАНК ХОЛБОСОН ХЭСЭГ --- */}
      {complainRef?.complainType === "MANUAL" && complainRef?.bankStatement && (
        <section>
          <SectionHeader title={"Банк"} />
          <Flex vertical gap={8}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Гүйлгээний ID" style={{ fontSize: 13 }}>
                {complainRef.bankStatement?.record_id || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Данс" style={{ fontSize: 13 }}>
                {complainRef.bankStatement?.account_number || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Дүн" style={{ fontSize: 13 }}>
                {complainRef.bankStatement?.amount
                  ? formatCurrency(complainRef.bankStatement.amount)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Огноо" style={{ fontSize: 13 }}>
                {formatDateTime(complainRef.bankStatement?.date_at) || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Төлөв" style={{ fontSize: 13 }}>
                {statusTag(complainRef.bankStatement?.purchase_status, "bank")}
              </Descriptions.Item>
              <Descriptions.Item
                label="Худалдан авалт"
                style={{ fontSize: 13 }}
              >
                {complainRef.bankStatement?.purchase_comment}
              </Descriptions.Item>

              <Descriptions.Item
                label="Хэрэглэгчийн данс"
                style={{ fontSize: 13 }}
              >
                {complainRef.bankStatement?.raw_data?.relatedAccount ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Тайлбар"
                span={2}
                style={{ fontSize: 13 }}
              >
                <p
                  style={{
                    whiteSpace: "pre-wrap",
                    margin: 0,
                    fontSize: 13,
                  }}
                >
                  {complainRef.bankStatement?.description || "-"}
                </p>
              </Descriptions.Item>
            </Descriptions>
          </Flex>
        </section>
      )}
    </Flex>
  );
}

export default DetailView;
